import { effectiveTranslatable } from './effective-translatable'
import { s } from '@w/schema'
import { RpcError } from '@w/rpc'
import type { Tel } from '@w/tel'
import { glossUnits, validateTranslatedUnit, type TranslationUnit } from '@w/content-blocks'
import { pageTranslationUnits } from './page-title-unit'
import { pageParentColumn, pageReadColumns, pageReadSchema } from '../db/page-read'
import type { WorkspaceDb } from '../db/workspace-tables'
import { decodeReadable, readableRowsOptions } from '../dead-letter-queue/item-readers'
import type { ItemReadResource } from '../dead-letter-queue/entries'
import { safe } from '@w/utils'

export type ProcessedPage = { version: number; units: number }

export type TranslateTexts = (input: {
  texts: string[]
  target: string
  format: 'text' | 'html'
  signal?: AbortSignal
}, tel: Tel) => Promise<{ text: string; detectedSourceLanguage?: string }[]>

type Pending = { text: string; blockId: string; hash: string; unit?: TranslationUnit }
export type PageTranslationReport = { sent: number }

export async function updatePageTranslations(input: {
  db: WorkspaceDb
  readResource?: ItemReadResource
  translate: TranslateTexts
  language: string
  pageIds?: string[]
  /** Explicit opens may refresh changed sources; maintenance only repairs unchanged sources. */
  revalidatePageIds?: string[]
  skipPageIds?: string[]
  /** Per page: the version last processed and how many units it had then (0 = nothing to translate; no repair needed). */
  processedPages?: Map<string, ProcessedPage>
  isClosed?: () => boolean
}, tel: Tel): Promise<PageTranslationReport> {
  const { language } = input
  const db = input.db.withTel(tel)
  const report: PageTranslationReport = { sent: 0 }
  if (input.isClosed?.()) return report
  const processed = new Map(input.processedPages)
  const versionQuery = db.query`SELECT ${db.pages.id}, ${pageParentColumn(db.pages)} AS parentId, ${db.pages.updatedAt}, ${db.pages.deletedAt}, coalesce(${db.pages.translatable}, 0) AS translatable FROM ${db.pages} ORDER BY ${db.pages.id}`
  const versionSchema = s.object({ id: db.pages.id.zodType, parentId: s.string().nullable(), updatedAt: db.pages.updatedAt.zodType, deletedAt: db.pages.deletedAt.zodType, translatable: s.number() })
  const versions = input.readResource
    ? await readableRowsOptions(input.readResource, versionQuery, versionSchema, 'page', 'PAGE_METADATA').queryFn()
    : await versionQuery.execute(versionSchema)
  const storedQuery = db.query`SELECT ${db.pageTranslations.all} FROM ${db.pageTranslations} WHERE ${db.pageTranslations.language.eq(language)}`
  const stored = input.readResource ? await (async () => {
    const { sql, params } = storedQuery.toSql()
    return decodeReadable(await safe(db.driver).query<Record<string, unknown>>(sql, params), db.pageTranslations.__selectSchema)
  })() : await storedQuery.execute(db.pageTranslations.__selectSchema)
  const livePages = effectiveTranslatable(versions.filter(page => !page.deletedAt).map(page => ({ id: page.id, parentId: page.parentId, translatable: page.translatable === 1 })))
  for (const row of stored) if ((!input.pageIds || input.pageIds.includes(row.pageId)) && !livePages.has(row.pageId)) await db.query`DELETE FROM ${db.pageTranslations} WHERE ${db.pageTranslations.pageId.eq(row.pageId)} AND ${db.pageTranslations.language.eq(language)}`.run()
  // Checkpoint membership means effectively marked in the last successful run.
  // Losing eligibility clears it even when the child's own timestamp is unchanged.
  for (const id of processed.keys()) if (!livePages.has(id)) processed.delete(id)
  const changed = versions.filter(page => {
    if (input.pageIds && !input.pageIds.includes(page.id)) return false
    if (!livePages.has(page.id)) return false
    if (input.skipPageIds?.includes(page.id)) {
      processed.delete(page.id)
      return false
    }
    const done = processed.get(page.id)
    if (done && input.revalidatePageIds && !input.revalidatePageIds.includes(page.id) && done.version !== page.updatedAt.getTime()) return false
    // A page with units but no stored rows lost its derived rows: repair it. A page with no units is never re-read.
    return done?.version !== page.updatedAt.getTime() || (done.units > 0 && !stored.some(row => row.pageId === page.id))
  })
  const changedQuery = db.query`SELECT ${pageReadColumns(db.pages)} FROM ${db.pages} WHERE ${db.pages.id.inArray(changed.map(page => page.id))} ORDER BY ${db.pages.id}`
  const pages = changed.length === 0 ? [] : input.readResource ? await readableRowsOptions(input.readResource, changedQuery, pageReadSchema(''), 'page', 'PAGE_DOCUMENT').queryFn() : await changedQuery.execute(pageReadSchema(''))
  for (const page of pages) {
    if (input.isClosed?.()) break
    processed.set(page.id, { version: page.updatedAt.getTime(), units: 0 })
    if (page.deletedAt || (page.content.kind !== 'note' && page.content.kind !== 'language')) continue
    const pending: Pending[] = [
      ...pageTranslationUnits({ title: page.title, children: page.content.children }).map(unit => ({ text: unit.html, blockId: unit.blockId, hash: unit.hash, unit })),
      ...glossUnits(page.content.children).map(unit => ({ text: unit.text, blockId: unit.blockId, hash: unit.hash })),
    ]
    processed.set(page.id, { version: page.updatedAt.getTime(), units: pending.length })
    const currentRows = stored.filter(row => row.pageId === page.id)
    for (const row of currentRows) if (!pending.some(unit => unit.blockId === row.blockId)) await db.query`DELETE FROM ${db.pageTranslations} WHERE ${db.pageTranslations.pageId.eq(page.id)} AND ${db.pageTranslations.blockId.eq(row.blockId)} AND ${db.pageTranslations.language.eq(language)}`.run()
    const missing = pending.filter(unit => !currentRows.some(row => row.blockId === unit.blockId && row.sourceHash === unit.hash))
    if (missing.length === 0) continue
    const translatedRows: { pageId: string; blockId: string; language: string; sourceHash: string; text: string | null; detectedSourceLanguage: string | null }[] = []
    const send = async (batch: Pending[]): Promise<Awaited<ReturnType<TranslateTexts>>> => {
      if (batch.length === 0 || input.isClosed?.()) return []
      report.sent += batch.length
      let result: (Awaited<ReturnType<TranslateTexts>>[number] | null)[]
      try {
        result = await input.translate({ texts: batch.map(unit => unit.text), target: language, format: batch[0].unit ? 'html' : 'text' }, tel)
      } catch (error) {
        if (!(error instanceof RpcError) || error.status !== 400) throw error
        if (input.isClosed?.()) return []
        if (batch.length > 1) {
          const isolated: Awaited<ReturnType<TranslateTexts>> = []
          for (const unit of batch) isolated.push(...await send([unit]))
          return isolated
        }
        result = [null]
      }
      if (input.isClosed?.()) return []
      if (result.length !== batch.length) throw new Error('Translation result count does not match input')
      for (const [index, unit] of batch.entries()) {
        const translated = result[index]
        const text = translated && (!unit.unit || validateTranslatedUnit(unit.unit, translated.text)) ? translated.text : null
        translatedRows.push({ pageId: page.id, blockId: unit.blockId, language, sourceHash: unit.hash, text, detectedSourceLanguage: translated?.detectedSourceLanguage ?? null })
      }
      return result.filter(translated => translated !== null)
    }
    // Each format is generated in one batch; all rows publish together below.
    await send(missing.filter(unit => unit.unit))
    await send(missing.filter(unit => !unit.unit))
    // Publish the page together after generation; never expose partially generated units.
    if (translatedRows.length > 0 && !input.isClosed?.()) {
      await db.query`${db.pageTranslations.insert(translatedRows)}
        ON CONFLICT(pageId,blockId,language) DO UPDATE SET sourceHash=excluded.sourceHash,text=excluded.text,detectedSourceLanguage=excluded.detectedSourceLanguage`.run()
    }
  }
  // Publish only after the entire run succeeds.
  if (!input.isClosed?.() && input.processedPages) {
    input.processedPages.clear()
    for (const [id, version] of processed) input.processedPages.set(id, version)
  }
  return report
}
